import styles from './BotReply.module.css';
import QuestionCard from './QuestionCard';
import type { ClarifyingQuestion } from '../types';
import { Link } from 'react-router-dom';

interface BotReplyProps {
  summary: string;
  questions: ClarifyingQuestion[];
  userAnswers: Record<string, string>;
  approved: boolean;
  revisionNote?: string;
  onAnswerChange: (id: string, answer: string) => void;
}

function summarizeForSlack(summary: string, revisionNote?: string): string[] {
  const bullets = [
    summary,
    'Sections: Goal, Users & core flows, Requirements, Screens, Data, Open questions.',
  ];
  if (revisionNote) {
    bullets.push(`Revised to include: ${revisionNote}`);
  } else {
    bullets.push('Defaults are editable above; reply *approved* to generate the PRD.');
  }
  return bullets;
}

export default function BotReply({
  summary,
  questions,
  userAnswers,
  approved,
  revisionNote,
  onAnswerChange,
}: BotReplyProps) {
  if (!approved) {
    return (
      <div className={styles.replyCard}>
        <p className={styles.summary} data-testid="prd-summary">
          Here is what I understood: <em>{summary}</em>. I have a few clarifying questions:
        </p>
        <div className={styles.questions}>
          {questions.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              answer={userAnswers[q.id] ?? q.defaultAnswer}
              onChange={(value) => onAnswerChange(q.id, value)}
            />
          ))}
        </div>
        <div className={styles.hint} data-testid="approve-hint">
          Reply <strong>approved</strong> when you are ready, or edit the defaults above.
        </div>
      </div>
    );
  }

  const bullets = summarizeForSlack(summary, revisionNote);

  return (
    <div className={styles.replyCard}>
      <div className={styles.prdSummary} data-testid="prd-summary">
        <h4>PRD generated</h4>
        <ul>
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>
      <Link
        to="/prd"
        className={styles.attachment}
        data-testid="prd-attachment"
      >
        <div className={styles.fileIcon}>MD</div>
        <div className={styles.fileMeta}>
          <span className={styles.fileName}>prd.md</span>
          <span className={styles.fileSize}>Slack Thread PRD</span>
        </div>
      </Link>
    </div>
  );
}
